import {
  ArcoDto,
  getGetNodoIconaImageQueryKey,
  NodoDto,
  PortaleDto,
  useGetAziende,
  useGetGrafoNodo,
  UserEsternoPermessoDto,
} from "@/api";
import CentraleIcon from "@/assets/centrale-icon2.png";
import AziendaIcon from "@/assets/company.png";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Box, IconButton, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { Handle, Position, ReactFlow } from "@xyflow/react";
import { Dictionary, keyBy, uniq } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AziendaPicker from "../AziendaPicker";
import { useLayoutedReactFlowNodesAndEdges } from "../Grafo/useLayoutedReactFlowNodesAndEdges";
import { useConfirmationDialog } from "@/utils/useConfirmationDialog";
import { portaleConfig } from "@/portaliConfig";

export default function ClienteNodiSelector({
  value,
  onChange,
}: {
  portali: PortaleDto[];
  value: UserEsternoPermessoDto[];
  onChange: (permessi: UserEsternoPermessoDto[]) => void;
}) {
  const { t } = useTranslation();

  const { data: aziendeData, isLoading: isLoadingAziende } = useGetAziende();

  const aziendaById = useMemo(() => {
    return keyBy(aziendeData?.data ?? [], (x) => x.id);
  }, [aziendeData]);

  const aziendeSelezionate = useMemo(() => {
    const nodiIds = uniq(value.map((x) => x.nodoId));

    return nodiIds
      .map((nodoId) => aziendaById[nodoId])
      .filter((x) => !!x) as NodoDto[];
  }, [aziendaById, value]);

  const [currentTabAziendaId, setCurrentTabAziendaId] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (isLoadingAziende) {
      return;
    }

    if (currentTabAziendaId === null) {
      if (aziendeSelezionate.length > 0) {
        setCurrentTabAziendaId(aziendeSelezionate[0].id);
      } else {
        setCurrentTabAziendaId(0);
      }
    }
  }, [currentTabAziendaId, aziendeSelezionate, isLoadingAziende]);

  useEffect(() => {
    if (currentTabAziendaId !== null && currentTabAziendaId !== 0) {
      const aziendaAncoraSelezionata = aziendeSelezionate.find(
        (x) => x.id === currentTabAziendaId
      );
      if (!aziendaAncoraSelezionata) {
        setCurrentTabAziendaId(0);
      }
    }
  }, [currentTabAziendaId, aziendeSelezionate]);

  const hasAtLeastOneAzienda = isLoadingAziende
    ? null
    : aziendeSelezionate.length > 0;

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        {hasAtLeastOneAzienda === true && (
          <Tabs
            value={currentTabAziendaId}
            onChange={(e, v) => {
              setCurrentTabAziendaId(v);
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {aziendeSelezionate.map((azienda) => {
              return (
                <Tab value={azienda.id} key={azienda.id} label={azienda.nome} />
              );
            })}

            <Tab
              value={0}
              sx={{ maxWidth: 20 }}
              label={
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-start"
                >
                  <Tooltip title={t("clienteDialog.aziende.aggiungi")}>
                    <AddIcon />
                  </Tooltip>
                </Box>
              }
            />
          </Tabs>
        )}

        <Box sx={{ width: "100%", height: "300px", mt: 1 }}>
          {currentTabAziendaId === null ? null : currentTabAziendaId === 0 ? (
            <AddAziendaTab
              onSelectAzienda={(azienda) => {
                const aziendaGiaSelezionata = aziendeSelezionate.find(
                  (x) => x.id === azienda.id
                );
                if (!aziendaGiaSelezionata) {
                  onChange([
                    ...value,
                    {
                      nodoId: azienda.id,
                      negaAccesso: false,
                    },
                  ]);
                }

                setCurrentTabAziendaId(azienda.id);
              }}
            />
          ) : (
            <AziendaTab
              key={currentTabAziendaId}
              azienda={aziendaById[currentTabAziendaId]}
              onChange={onChange}
              value={value}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

function AddAziendaTab({
  onSelectAzienda,
}: {
  onSelectAzienda: (nodo: NodoDto) => void;
}) {
  const { t } = useTranslation();

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      p={6}
      rowGap={2}
    >
      <Typography variant="body2">
        {t("clienteDialog.aziende.seleziona")}
      </Typography>

      <Box>
        <AziendaPicker
          type="aziende"
          onChange={(e, x) => {
            if (x) {
              onSelectAzienda(x);
            }
          }}
        />
      </Box>
    </Box>
  );
}

const nodeTypes = {
  default: NodoPermesso,
};

const proOptions = { hideAttribution: true };

function AziendaTab({
  azienda,
  value,
  onChange,
}: {
  azienda: NodoDto;
  value: UserEsternoPermessoDto[];
  onChange: (permessi: UserEsternoPermessoDto[]) => void;
}) {
  const { data: grafoData } = useGetGrafoNodo(azienda.id);

  const ruleByNodoId = useMemo(() => {
    return keyBy(value, (x) => x.nodoId);
  }, [value]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const additionalNodeDataProps = useMemo(() => {
    return {
      ruleByNodoId,
      onChange: onChangeRef.current,
    };
  }, [ruleByNodoId]);

  const { nodes, edges } = useLayoutedReactFlowNodesAndEdges(
    azienda,
    grafoData?.data ?? null,
    additionalNodeDataProps,
    true
  );

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        style={{
          border: "1px solid gray",
          borderRadius: "5px",
        }}
        nodeTypes={nodeTypes}
        proOptions={proOptions}
      />
    </Box>
  );
}

function NodoPermesso({
  data: {
    nodo,
    ruleByNodoId,
    onChange,
    archiByDestinazioneId,
    archiBySorgenteId,
  },
}: {
  data: {
    nodo: NodoDto;
    ruleByNodoId: Dictionary<UserEsternoPermessoDto>;
    onChange: (permessi: UserEsternoPermessoDto[]) => void;
    archiByDestinazioneId: Dictionary<ArcoDto[]>;
    archiBySorgenteId: Dictionary<ArcoDto[]>;
  };
}) {
  const { t } = useTranslation();
  const icona = nodo.contenitoreIcona;
  const iconaTitolo =
    icona?.nome ??
    (nodo.tipo === "Azienda"
      ? t("clienteDialog.aziende.azienda")
      : t("clienteDialog.aziende.centrale"));
  const iconaSrc =
    nodo.tipo === "Azienda"
      ? AziendaIcon
      : icona?.id
      ? getGetNodoIconaImageQueryKey(icona.id)[0]
      : CentraleIcon;

  const haAccesso = useMemo(() => {
    if (nodo.tipo === "Azienda") {
      return true;
    }

    let toCheckList = [nodo.id];
    do {
      for (const toCheck of toCheckList) {
        const denyRule = ruleByNodoId[toCheck];
        if (denyRule?.negaAccesso) {
          return false;
        }
      }

      toCheckList = toCheckList.flatMap((id) => {
        return (archiByDestinazioneId[id] ?? []).map((x) => x.sorgenteId!);
      });
    } while (toCheckList.length > 0);

    return true;
  }, [nodo, ruleByNodoId, archiByDestinazioneId]);

  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  const handleClick = () => {
    if (haAccesso) {
      if (nodo.tipo === "Azienda") {
        askConfirmation({
          title: t("clienteDialog.aziende.bloccaTitolo"),
          message: t("clienteDialog.aziende.bloccaMessaggio", {
            azienda: nodo.nome,
          }),
          onConfirm: () => {
            const newRules = { ...ruleByNodoId };

            // elimino l'accesso all'azienda
            delete newRules[nodo.id];

            // devo rimuovere tutte le regole che potrebbero stare al di sotto di me
            removeSubtreeRules(archiBySorgenteId, nodo, newRules);

            onChange(Object.values(newRules));
          },
        });
      } else {
        // rimuovo l'accesso al nodo introducendo una regola
        const newRules = { ...ruleByNodoId };
        newRules[nodo.id] = {
          nodoId: nodo.id,
          negaAccesso: true,
        };

        // devo rimuovere tutte le regole che potrebbero stare al di sotto di me
        removeSubtreeRules(archiBySorgenteId, nodo, newRules);

        onChange(Object.values(newRules));
      }
    } else {
      // garantisco accesso al nodo rimuovendo la regola
      const newRules = { ...ruleByNodoId };
      delete newRules[nodo.id];
      onChange(Object.values(newRules));
    }
  };

  const hasDenyRule = ruleByNodoId[nodo.id]?.negaAccesso === true;
  const showToggleButton = haAccesso || hasDenyRule;

  return (
    <>
      {nodo.tipo === "Azienda" && confirmationDialog}

      {nodo.tipo === "Contenitore" && (
        <Handle type="target" position={Position.Top} />
      )}

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        p={1}
        sx={{
          width: "100%",
          height: "100%",
          backgroundColor: haAccesso
            ? "lightgreen"
            : hasDenyRule
            ? "#ff000088"
            : "#ff000055",
          position: "relative",
        }}
      >
        {showToggleButton && (
          <Box
            sx={{ position: "absolute", top: 0, right: 0 }}
            onClick={handleClick}
          >
            <IconButton size="small">
              {haAccesso ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </Box>
        )}

        <img src={iconaSrc} width={24} alt={iconaTitolo} />

        <Box
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "100%",
          }}
        >
          {nodo.nome}
        </Box>
      </Box>

      <Handle type="source" position={Position.Bottom} />
    </>
  );
}

function removeSubtreeRules(
  archiBySorgenteId: Dictionary<ArcoDto[]>,
  nodo: NodoDto,
  rules: Dictionary<UserEsternoPermessoDto>
) {
  let toCleanList = archiBySorgenteId[nodo.id] ?? [];
  do {
    const newToCleanList: ArcoDto[] = [];

    for (const toClean of toCleanList) {
      delete rules[toClean.destinazioneId!];

      const children = archiBySorgenteId[toClean.destinazioneId!] ?? [];
      for (const child of children) {
        newToCleanList.push(child);
      }
    }

    toCleanList = newToCleanList;
  } while (toCleanList.length > 0);
}
